import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import ProcessActions from '../../actions/generations/process.actions';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';

@Injectable()
export class ProcessEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly store = inject(Store);
  private readonly loaderService = inject(AppLoaderService);
  private readonly translateService = inject(TranslateService);
  private readonly utilService = inject(UtilService);

  getAllProcesses$ = createEffect(() =>
    this.actions.pipe(
      ofType(ProcessActions.getAllProcess),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => ProcessActions.setProcess({ data: response })),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
  createProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ProcessActions.createProcess),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_ADDED_SUCCESSFULLY', { component: 'Process' })
            );
            return ProcessActions.createProcessSuccess({ data: response });
          }),
          catchError((error) => {
            return of(ProcessActions.createProcessFailure({ error }));
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
  createProcessSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ProcessActions.createProcessSuccess),
      map((response: any) => ProcessActions.addProcessToState({ data: response.data }))
    )
  );

  createProcessFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ProcessActions.createProcessFailure),
        tap((response: any) => {
          if (response.error?.error) {
            this.utilService.showError(this.translateService.instant(response.error.error.ERROR_CODE));
            return;
          }
          this.utilService.showError('Failed to create process. Please try again.');
        })
      ),
    { dispatch: false }
  );
  updateProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ProcessActions.updateProcess),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_UPDATED_SUCCESSFULLY', { component: 'Process' })
            );
            return ProcessActions.updateProcessInState({ data: response });
          }),
          catchError((error) => {
            console.error('Error updating process:', error);
            return EMPTY;
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
  deleteProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ProcessActions.deleteProcess),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables, parentNodeSid } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_DELETED_SUCCESSFULLY', { component: 'Process' })
            );
            return ProcessActions.deleteStateProcess({ data: { parentNodeSid, sid: urlPathVariables.componentId } });
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
}
